import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { RMA_REASON_LABELS, RMA_STATUS_LABELS, CP_SERVICE_LABELS } from '../types';
import { getRmaLabelUrl } from '../api';
import type { RmaOrder } from '../types';

interface Props {
  rma: RmaOrder;
}

// ─── Style constants ────────────────────────────────────────────────────────

const NAVY = '#0f172a';
const SLATE_700 = '#334155';
const SLATE_500 = '#64748b';
const SLATE_400 = '#94a3b8';
const SLATE_200 = '#e2e8f0';
const SLATE_50 = '#f8fafc';
const WHITE = '#ffffff';

const S: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: '9pt',
    color: SLATE_700,
    background: WHITE,
    width: '100%',
    boxSizing: 'border-box',
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },

  accentBar: {
    background: NAVY,
    height: '6pt',
    width: '100%',
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },

  body: {
    padding: '6pt 0 0 0',
  },

  // ── Header ──────────────────────────────────────────────────────────────
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10pt',
  },

  logoBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3pt',
  },

  logo: {
    height: '72pt',
    width: 'auto',
    display: 'block',
  },

  logoSubtext: {
    fontSize: '7pt',
    color: SLATE_500,
    marginTop: '3pt',
    letterSpacing: '0.2pt',
  },

  rmaBlock: {
    textAlign: 'right',
  },

  rmaEyebrow: {
    fontSize: '6.5pt',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '1.5pt',
    color: SLATE_400,
    marginBottom: '3pt',
  },

  rmaNumber: {
    fontSize: '20pt',
    fontWeight: '800',
    fontFamily: 'Courier New, monospace',
    color: NAVY,
    lineHeight: 1,
    marginBottom: '4pt',
  },

  rmaDate: {
    fontSize: '7.5pt',
    color: SLATE_500,
    marginBottom: '5pt',
  },

  statusBadge: {
    display: 'inline-block',
    padding: '2pt 8pt',
    background: NAVY,
    borderRadius: '20pt',
    fontSize: '6.5pt',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.8pt',
    color: WHITE,
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },

  // ── Divider ─────────────────────────────────────────────────────────────
  divider: {
    borderTop: '1.5pt solid ' + NAVY,
    marginBottom: '14pt',
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },

  // ── 3-col info strip ────────────────────────────────────────────────────
  infoStrip: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    marginBottom: '16pt',
  },

  infoCol: {
    paddingRight: '14pt',
  },

  infoColMid: {
    paddingLeft: '14pt',
    paddingRight: '14pt',
    borderLeft: '0.75pt solid ' + SLATE_200,
    borderRight: '0.75pt solid ' + SLATE_200,
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },

  infoColRight: {
    paddingLeft: '14pt',
  },

  infoLabel: {
    fontSize: '6.5pt',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.8pt',
    color: SLATE_400,
    marginBottom: '5pt',
  },

  infoTitle: {
    fontSize: '10.5pt',
    fontWeight: '700',
    color: NAVY,
    marginBottom: '3pt',
    lineHeight: 1.2,
  },

  infoText: {
    fontSize: '8.5pt',
    color: SLATE_700,
    lineHeight: 1.55,
  },

  infoMono: {
    fontFamily: 'Courier New, monospace',
    fontSize: '8.5pt',
    fontWeight: '700',
    color: NAVY,
    letterSpacing: '0.3pt',
  },

  infoMuted: {
    fontSize: '8pt',
    color: SLATE_500,
    fontStyle: 'italic',
    marginTop: '2pt',
    lineHeight: 1.4,
  },

  // ── Section label ────────────────────────────────────────────────────────
  sectionLabel: {
    fontSize: '6.5pt',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.8pt',
    color: SLATE_400,
    marginBottom: '5pt',
  },

  // ── Items table ──────────────────────────────────────────────────────────
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '8.5pt',
    marginBottom: '14pt',
    border: '0.5pt solid ' + SLATE_200,
  },

  thead: {
    background: NAVY,
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },

  th: {
    padding: '5pt 7pt',
    textAlign: 'left',
    fontWeight: '600',
    fontSize: '7.5pt',
    letterSpacing: '0.3pt',
    color: WHITE,
    borderRight: '0.5pt solid #1e293b',
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },

  thLast: {
    padding: '5pt 7pt',
    textAlign: 'left',
    fontWeight: '600',
    fontSize: '7.5pt',
    letterSpacing: '0.3pt',
    color: WHITE,
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },

  tdEven: {
    padding: '5pt 7pt',
    borderBottom: '0.5pt solid ' + SLATE_200,
    borderRight: '0.5pt solid ' + SLATE_200,
    background: WHITE,
    verticalAlign: 'top',
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },

  tdOdd: {
    padding: '5pt 7pt',
    borderBottom: '0.5pt solid ' + SLATE_200,
    borderRight: '0.5pt solid ' + SLATE_200,
    background: SLATE_50,
    verticalAlign: 'top',
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },

  emptyCell: {
    padding: '10pt 7pt',
    textAlign: 'center',
    color: SLATE_400,
    fontStyle: 'italic',
    background: WHITE,
  },

  // ── Instructions ─────────────────────────────────────────────────────────
  instructionsRule: {
    borderTop: '0.5pt solid ' + SLATE_200,
    marginBottom: '8pt',
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },

  instructionsRow: {
    display: 'flex',
    gap: '18pt',
    fontSize: '7.5pt',
    color: SLATE_700,
    marginBottom: '10pt',
    flexWrap: 'wrap',
  },

  instructionsStep: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '4pt',
  },

  stepNum: {
    fontSize: '6pt',
    fontWeight: '700',
    color: WHITE,
    background: NAVY,
    borderRadius: '50%',
    width: '8pt',
    height: '8pt',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },

  // ── Footer ───────────────────────────────────────────────────────────────
  footer: {
    borderTop: '0.5pt solid ' + SLATE_200,
    paddingTop: '6pt',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '7pt',
    color: SLATE_400,
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export function RmaPrintDocument({ rma }: Props) {
  const { t } = useTranslation('operations');
  const printedAt = format(new Date(), 'MMM d, yyyy');
  const issuedAt = format(new Date(rma.createdAt), 'MMM d, yyyy');
  const steps = [
    t('rma.print.step1', 'Pack item(s) securely to prevent transit damage.'),
    t('rma.print.step2', 'Include a printed copy of this document inside the package.'),
    t('rma.print.step3', 'Affix the Canada Post return label to the outside.'),
    t('rma.print.step4', 'Drop off at any Canada Post outlet or authorized location.'),
    t('rma.print.step5', 'Keep this document and tracking PIN for your records: {{pin}}.', {
      pin: rma.cpTrackingPin ?? t('rma.print.step5NoPin', 'See label'),
    }),
    t('rma.print.step6', 'Failure to return the item(s) within 30 days will result in a charge for the full replacement value.'),
  ];

  return (
    <>
    <div className="hidden print:block" style={S.page}>

      {/* ── Top accent bar ── */}
      <div style={S.accentBar} />

      <div style={S.body}>

        {/* ── Header ── */}
        <div style={S.header}>

          {/* Left: logo + contact */}
          <div style={S.logoBlock}>
            <img
              src="/atek/branding/logos/atek-logo-main-dark-large.png"
              alt="ATEK"
              style={S.logo}
            />
          </div>

          {/* Right: RMA ref block */}
          <div style={S.rmaBlock}>
            <div style={S.rmaEyebrow}>{t('rma.print.eyebrow', 'Return Merchandise Authorization')}</div>
            <div style={S.rmaNumber}>{rma.rmaNumber}</div>
            <div style={S.rmaDate}>{t('rma.print.issued', 'Issued: {{date}}', { date: issuedAt })}</div>
          </div>
        </div>

        {/* ── Full-width rule ── */}
        <div style={S.divider} />

        {/* ── 3-col info strip ── */}
        <div style={S.infoStrip}>

          {/* Column 1 — Customer */}
          <div style={S.infoCol}>
            <div style={S.infoLabel}>{t('rma.print.customer', 'Customer')}</div>
            {rma.accountName && (
              <div style={S.infoTitle}>{rma.accountName}</div>
            )}
            {rma.quoteNumber && (
              <div style={S.infoText}>
                {t('rma.print.quoteNo', 'Quote #{{number}}', { number: rma.quoteNumber })}
              </div>
            )}
            <div style={{ ...S.infoText, marginTop: '4pt' }}>
              <strong>{t('rma.print.reasonLabel', 'Reason:')}</strong>&nbsp;{t(`rma.reason.${rma.reason}`, RMA_REASON_LABELS[rma.reason])}
            </div>
            {rma.reasonNotes && (
              <div style={S.infoMuted}>{rma.reasonNotes}</div>
            )}
          </div>

          {/* Column 2 — Return to */}
          <div style={S.infoColMid}>
            <div style={S.infoLabel}>{t('rma.print.returnTo', 'Return To')}</div>
            <div style={S.infoTitle}>{rma.returnAddressName}</div>
            {rma.returnAddressCompany && (
              <div style={S.infoText}>{rma.returnAddressCompany}</div>
            )}
            <div style={S.infoText}>{rma.returnAddressLine1}</div>
            {rma.returnAddressLine2 && (
              <div style={S.infoText}>{rma.returnAddressLine2}</div>
            )}
            <div style={S.infoText}>
              {rma.returnAddressCity}, {rma.returnAddressProvince}&nbsp;
              {rma.returnAddressPostalCode}
            </div>
          </div>

          {/* Column 3 — Shipping */}
          <div style={{ ...S.infoColRight, display: 'flex', gap: '10pt', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={S.infoLabel}>{t('rma.print.shipping', 'Shipping')}</div>
              {rma.cpServiceCode ? (
                <div style={{ ...S.infoText, marginBottom: '5pt' }}>
                  {t('rma.print.canadaPostService', 'Canada Post — {{service}}', {
                    service: t(`rma.service.${rma.cpServiceCode}`, CP_SERVICE_LABELS[rma.cpServiceCode]),
                  })}
                </div>
              ) : null}
              {rma.cpTrackingPin ? (
                <>
                  <div style={{ ...S.infoLabel, marginBottom: '3pt' }}>{t('rma.print.trackingPin', 'Tracking PIN')}</div>
                  <div style={S.infoMono}>{rma.cpTrackingPin}</div>
                </>
              ) : (
                <div style={S.infoMuted}>{t('rma.print.labelNotGenerated', 'Label not yet generated')}</div>
              )}
            </div>

            {/* QR code — generated from tracking PIN */}
            {rma.cpTrackingPin && (
              <div style={{ textAlign: 'center', flexShrink: 0 }}>
                <QRCodeSVG
                  value={rma.cpTrackingPin}
                  size={72}
                  bgColor="#ffffff"
                  fgColor="#0f172a"
                  level="M"
                  style={{ display: 'block' } as React.CSSProperties}
                />
                <div style={{ fontSize: '6pt', color: SLATE_400, marginTop: '3pt' }}>
                  {t('rma.print.scanToTrack', 'Scan to track')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Items table ── */}
        <div style={S.sectionLabel}>
          {t('rma.print.itemsHeading', 'Items Being Returned —')}&nbsp;{t(
            rma.items.length === 1 ? 'rma.print.itemCount_one' : 'rma.print.itemCount_other',
            '{{count}} item(s)',
            { count: rma.items.length },
          )}
        </div>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              <th style={{ ...S.th, width: '11%' }}>{t('rma.items.col.sku', 'SKU')}</th>
              <th style={{ ...S.th, width: '13%' }}>{t('rma.print.colSerial', 'Serial / DevID')}</th>
              <th style={{ ...S.th, width: '22%' }}>{t('rma.items.col.product', 'Product Name')}</th>
              <th style={{ ...S.th, width: '5%', textAlign: 'center' }}>{t('rma.items.col.qty', 'Qty')}</th>
              <th style={{ ...S.th, width: '12%' }}>{t('rma.items.col.reason', 'Reason')}</th>
              <th style={{ ...S.th, width: '12%' }}>{t('rma.items.col.condition', 'Condition')}</th>
              <th style={{ ...S.th, width: '12%' }}>{t('rma.items.col.resolution', 'Resolution')}</th>
              <th style={{ ...S.thLast }}>{t('rma.items.col.notes', 'Notes')}</th>
            </tr>
          </thead>
          <tbody>
            {rma.items.length === 0 ? (
              <tr>
                <td colSpan={8} style={S.emptyCell}>
                  {t('rma.print.noItems', 'No items added')}
                </td>
              </tr>
            ) : (
              rma.items.map((item, i) => {
                const td = i % 2 === 0 ? S.tdEven : S.tdOdd;
                return (
                  <tr key={item.id}>
                    <td style={{ ...td, fontFamily: 'Courier New, monospace', fontSize: '8pt' }}>
                      {item.sku}
                    </td>
                    <td style={{ ...td, fontFamily: 'Courier New, monospace', fontSize: '8pt' }}>
                      {item.serialNumber || '—'}
                    </td>
                    <td style={td}>{item.productName || '—'}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{item.quantity ?? 1}</td>
                    <td style={td}>
                      {item.reason ? t(`rma.reason.${item.reason}`, RMA_REASON_LABELS[item.reason]) : '—'}
                    </td>
                    <td style={td}>
                      {item.conditionOnReceipt
                        ? t(`rma.condition.${item.conditionOnReceipt}`, item.conditionOnReceipt)
                        : '—'}
                    </td>
                    <td style={td}>
                      {item.itemResolution
                        ? t(`rma.resolution.${item.itemResolution}`, item.itemResolution)
                        : '—'}
                    </td>
                    <td style={{ ...td, borderRight: 'none' }}>{item.itemNotes || '—'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* ── Instructions ── */}
        <div style={S.instructionsRule} />
        <div style={{ ...S.sectionLabel, marginBottom: '6pt' }}>
          {t('rma.print.instructionsTitle', 'Instructions for the Customer')}
        </div>
        <ul style={{ margin: 0, paddingLeft: '14pt', lineHeight: '1.8', fontSize: '8.5pt', color: SLATE_700, marginBottom: '10pt' }}>
          {steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ul>

        {/* ── Footer ── */}
        <div style={S.footer}>
          <span>
            ATEK &nbsp;&middot;&nbsp; atek.io &nbsp;&middot;&nbsp; +1 855 982-2835 &nbsp;&middot;&nbsp; {t('rma.print.confidential', 'Confidential')}
          </span>
          <span style={{ fontFamily: 'Courier New, monospace', letterSpacing: '0.3pt' }}>
            {rma.rmaNumber} &nbsp;&mdash;&nbsp; {t(`rma.status.${rma.status}`, RMA_STATUS_LABELS[rma.status])}
          </span>
          <span>{t('rma.print.printed', 'Printed: {{date}}', { date: printedAt })}</span>
        </div>

      </div>{/* end body */}

    </div>

    {/* Page 2: Canada Post label */}
    {(rma.cpLabelUrl || rma.cpTrackingPin) && (
      <iframe
        src={getRmaLabelUrl(rma.id)}
        title={t('rma.print.labelFrameTitle', 'Canada Post Shipping Label')}
        className="hidden print:block"
        style={{
          width: '100%',
          height: '100vh',
          border: 'none',
          pageBreakBefore: 'always',
          breakBefore: 'page',
        } as React.CSSProperties}
      />
    )}

    </>
  );
}
